import { isNotNil, skipNil } from "@w11k/rx-ninja";
import { immerable } from "immer";
import { ReplaySubject } from "rxjs";
import { take } from "rxjs/operators";
import { CommandsInvoker, createReducerFromCommandsInvoker } from "./commands";
import { deepFreeze } from "./deep-freeze";
import { isTyduxDevelopmentModeEnabled } from "./development";
import { deregisterFacadeCommands, registerFacadeCommands } from "./global-facade-registry";
import { getGlobalStore } from "./store-global";
import { functionNamesDeep, functionNamesShallow, selectToObservable } from "./utils";
/**
 * Tydux Facade
 */
var Facade = /** @class */ (function () {
    function Facade(mountPointOrName, commands, initialState) {
        var _this = this;
        this.destroyedSubject = new ReplaySubject(1);
        this.destroyedState = false;
        this.bufferedStateChanges = 0;
        this.reduxStoreStateSubject = new ReplaySubject(1);
        if (typeof mountPointOrName === "string") {
            this.facadeId = mountPointOrName;
            this.mountPoint = getGlobalStore().createDeepMountPoint(mountPointOrName);
        }
        else {
            this.facadeId = mountPointOrName.sliceName;
            this.mountPoint = mountPointOrName;
        }
        this.mountPoint.destroySubject
            .pipe(take(1))
            .subscribe(function () { return _this.destroy(); });
        registerFacadeCommands(this.facadeId, commands);
        var commandsInvoker = new CommandsInvoker(commands);
        this.commands = this.createCommandsProxy(commandsInvoker);
        this.mountPoint.addReducer(this.createReducerFromCommandsInvoker(commandsInvoker));
        this.setState(this.mountPoint.getState());
        this.reduxStoreStateSubject.next(this.state);
        if (initialState !== undefined) {
            var initialFacadeStateAction_1 = this.createActionName("@@SET_STATE");
            this.mountPoint.addReducer(function (state, action) {
                if (action.type === initialFacadeStateAction_1) {
                    var stateValue = action.initialState;
                    _this.setState(stateValue);
                    _this.reduxStoreStateSubject.next(_this.state);
                    return _this.mountPoint.setState(state, stateValue);
                }
                return state;
            });
            // Determine the initial value. Separate logic for promises and non-promises so that
            // facades with a non-promise initial value are initialized synchronously.
            if (initialState instanceof Promise) {
                this.bufferedStateChanges++;
                initialState
                    .then(function (value) {
                    _this.mountPoint.dispatch({ type: initialFacadeStateAction_1, initialState: value });
                })
                    .finally(function () { return _this.bufferedStateChanges--; });
            }
            else {
                var initialStateValue = initialState instanceof Function
                    ? initialState()
                    : initialState;
                this.mountPoint.dispatch({
                    type: initialFacadeStateAction_1,
                    initialState: initialStateValue
                });
            }
        }
        this.mountPointSubscription = this.mountPoint.subscribe(function () {
            var currentState = Object.assign({}, _this.mountPoint.getState());
            _this.bufferedStateChanges++;
            _this.setState(currentState);
            // trigger micro task to ease reentrant code
            Promise.resolve().then(function () {
                _this.bufferedStateChanges--;
                _this.reduxStoreStateSubject.next(currentState);
            });
        });
        this.moveMethodsFromPrototypeToInstance();
    }
    Object.defineProperty(Facade.prototype, "state", {
        get: function () {
            return this._state;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Completes all observables returned by this store. Once this method gets called,
     * dispatched actions won't have an effect.
     */
    Facade.prototype.destroy = function () {
        this.mountPointSubscription();
        this.mountPoint.freeSlicePath();
        this.destroyedState = true;
        this.reduxStoreStateSubject.complete();
        this.destroyedSubject.next(true);
        deregisterFacadeCommands(this.facadeId);
    };
    // noinspection JSUnusedGlobalSymbols
    /**
     * Delegate to Store#destroy() for Angular.
     */
    // tslint:disable-next-line:use-life-cycle-interface use-lifecycle-interface
    Facade.prototype.ngOnDestroy = function () {
        this.destroy();
    };
    Facade.prototype.observeDestroyed = function () {
        return this.destroyedSubject.asObservable();
    };
    Facade.prototype.isDestroyed = function () {
        return this.destroyedState;
    };
    Facade.prototype.hasBufferedStateChanges = function () {
        return this.bufferedStateChanges > 0;
    };
    Facade.prototype.select = function (selector) {
        return selectToObservable(this.reduxStoreStateSubject, selector);
    };
    /**
     * - operates on the micro-task queue
     * - only emits values when they change (identity-based)
     * - only emits nonNil values (null or undefined)
     */
    Facade.prototype.selectNonNil = function (selector) {
        return selectToObservable(this.reduxStoreStateSubject, selector)
            .pipe(skipNil());
    };
    Facade.prototype.createActionName = function (mutatorMethodName) {
        return "[".concat(this.facadeId, "] ").concat(mutatorMethodName);
    };
    Facade.prototype.createMountPoint = function (slice) {
        var mountPoint = this.mountPoint.tyduxStore.createDeepMountPoint(this.facadeId + "." + String(slice));
        this.observeDestroyed()
            .pipe(take(1))
            .subscribe(function () { return mountPoint.destroySubject.next(); });
        return mountPoint;
    };
    Facade.prototype.setState = function (state) {
        if (isNotNil(state) && ![Array, Map, Set, Object].includes(state.constructor)) {
            state.constructor[immerable] = true;
        }
        this._state = isTyduxDevelopmentModeEnabled() ? deepFreeze(state) : state;
    };
    Facade.prototype.moveMethodsFromPrototypeToInstance = function () {
        var methodNamesUntilStoreParent = [];
        var level = this;
        while (level instanceof Facade) {
            methodNamesUntilStoreParent.push.apply(methodNamesUntilStoreParent, functionNamesShallow(level));
            level = Object.getPrototypeOf(level);
        }
        var self = this;
        var _loop_1 = function (fnMemberName) {
            var method = this_1[fnMemberName];
            this_1[fnMemberName] = function () {
                return method.apply(self, arguments);
            };
        };
        var this_1 = this;
        for (var _i = 0, methodNamesUntilStoreParent_1 = methodNamesUntilStoreParent; _i < methodNamesUntilStoreParent_1.length; _i++) {
            var fnMemberName = methodNamesUntilStoreParent_1[_i];
            _loop_1(fnMemberName);
        }
    };
    Facade.prototype.createCommandsProxy = function (commandsInvoker) {
        var proxyObj = {};
        var _loop_2 = function (mutatorMethodName) {
            var self_1 = this_2;
            proxyObj[mutatorMethodName] = function () {
                var actionType = self_1.createActionName(mutatorMethodName);
                var args = Array.prototype.slice.call(arguments);
                var mutatorAction = { type: actionType, payload: args };
                return self_1.mountPoint.dispatch(mutatorAction);
            };
        };
        var this_2 = this;
        // const protoOfCommandsInstance = Object.getPrototypeOf(commandsInvoker.commands);
        for (var _i = 0, _a = functionNamesDeep(commandsInvoker.commands); _i < _a.length; _i++) {
            var mutatorMethodName = _a[_i];
            _loop_2(mutatorMethodName);
        }
        return proxyObj;
    };
    Facade.prototype.createReducerFromCommandsInvoker = function (commandsInvoker) {
        var _this = this;
        var mutatorReducer = createReducerFromCommandsInvoker(this.facadeId, commandsInvoker);
        return function (state, action) {
            var preLocalState = _this.mountPoint.extractState(state);
            if (_this.destroyedState || !_this.isActionForThisFacade(action)) {
                return state;
            }
            var postLocalState = mutatorReducer(preLocalState, action);
            state = _this.mountPoint.setState(state, postLocalState);
            return state;
        };
    };
    Facade.prototype.isActionForThisFacade = function (action) {
        if (typeof action.type !== "string") {
            return false;
        }
        return action.type.indexOf("[".concat(this.facadeId, "] ")) === 0;
    };
    return Facade;
}());
export { Facade };
